import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Trash2, GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * A single draggable array-item row: drag handle, the rendered item fields,
 * and a remove button. Mirrors the static row styling but adds reordering via
 * @dnd-kit (same pattern as SortableOptionRow / SelectOptionsEditor).
 * @param props - Row id, item content, remove handler, and control visibility flag
 * @returns The rendered sortable row
 */
export function SortableArrayItem({
  id,
  children,
  onRemove,
  showControls,
}: {
  id: string;
  children: React.ReactNode;
  onRemove: () => void;
  showControls: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-start gap-1.5 rounded-md border border-border/60 bg-background/40 px-2 py-1 transition hover:shadow-sm",
        isDragging && "z-10 opacity-60 shadow-sm",
      )}
    >
      {showControls && (
        <button
          type="button"
          aria-label="Drag to reorder"
          className="mt-1 flex h-7 w-7 shrink-0 cursor-grab touch-manipulation items-center justify-center rounded-md text-muted-foreground/50 transition-colors hover:text-muted-foreground active:cursor-grabbing focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="min-w-0 flex-1 space-y-1.5">{children}</div>
      {showControls && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mt-1 h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={onRemove}
                aria-label="Remove this item"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Remove this item</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
